import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { requireAppCheck } from '@jfw/angular/shared/app-check';
import { of } from 'rxjs';

// IMPORTANT!!!!!
// This is shared with E-Z Tux
// PLATFORM_DP_ENVIRONMENT cannot be injected
// Use WholesaleOrderService for methods only used by Platform/Dealer Portals

@Injectable({
  providedIn: 'root',
})
export class WholesaleOrderInfoService {
  private readonly http = inject(HttpClient);

  // Keep track of which form array indexes are pending undelete
  public readonly pendingUndeleteIndexes = signal<number[]>([]);

  public addPendingUndeleteIndex(index: number): void {
    this.pendingUndeleteIndexes.update((indexes) => {
      if (!indexes.includes(index)) {
        return [...indexes, index];
      }
      return indexes;
    });
  }

  public removePendingUndeleteIndex(index: number): void {
    this.pendingUndeleteIndexes.update((indexes) =>
      indexes.filter((i) => i !== index),
    );
  }

  public clearPendingUndeleteIndexes(): void {
    this.pendingUndeleteIndexes.set([]);
  }

  public isIndexPendingUndelete(index: number): boolean {
    return this.pendingUndeleteIndexes().includes(index);
  }

  // Keep track of which form array indexes are pending delete
  public readonly pendingDeleteIndexes = signal<number[]>([]);

  public addPendingDeleteIndex(index: number): void {
    this.pendingDeleteIndexes.update((indexes) => {
      if (!indexes.includes(index)) {
        return [...indexes, index];
      }
      return indexes;
    });
  }

  public removePendingDeleteIndex(index: number): void {
    this.pendingDeleteIndexes.update((indexes) =>
      indexes.filter((i) => i !== index),
    );
  }

  public clearPendingDeleteIndexes(): void {
    this.pendingDeleteIndexes.set([]);
  }

  public isIndexPendingDelete(index: number): boolean {
    return this.pendingDeleteIndexes().includes(index);
  }

  // Keep track of which form array indexes are pending edit
  public readonly pendingEditIndexes = signal<number[]>([]);

  public addPendingEditIndex(index: number): void {
    this.pendingEditIndexes.update((indexes) => {
      if (!indexes.includes(index)) {
        return [...indexes, index];
      }
      return indexes;
    });
  }

  public removePendingEditIndex(index: number): void {
    this.pendingEditIndexes.update((indexes) =>
      indexes.filter((i) => i !== index),
    );
  }

  public clearPendingEditIndexes(): void {
    this.pendingEditIndexes.set([]);
  }

  public isIndexPendingEdit(index: number): boolean {
    return this.pendingEditIndexes().includes(index);
  }

  public getOrderInfoByIntNum(apiUrl: string, intNum: number) {
    if (!intNum) {
      return of(null);
    }

    const orderSearchUrl = `${apiUrl}/order-detail/${intNum}`;

    return this.http.get(orderSearchUrl, {
      ...requireAppCheck,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.getCookie('ezt-api-token')}`, // Needed for EZ-Tux; Dealer/Platform Portals will overwrite this with Firebase auth token via interceptor
      },
    });
  }

  private getCookie(name: string): string {
    let ca: Array<string> = document.cookie.split(';');
    let caLen: number = ca.length;
    let cookieName = `${name}=`;
    let c: string;

    for (let i: number = 0; i < caLen; i += 1) {
      c = ca[i].replace(/^\s+/g, '');
      if (c.indexOf(cookieName) == 0) {
        return c.substring(cookieName.length, c.length);
      }
    }
    return '';
  }
}
